import { BankCellForm } from '@/shared/lib/forms/asset-form/bank-cell';
import { BondsForm } from '@/shared/lib/forms/asset-form/bonds';
import { BrokerageAccountForm } from '@/shared/lib/forms/asset-form/brokerage-account';
import { CentralizedWalletForm } from '@/shared/lib/forms/asset-form/centralized-wallet';
import { CommercialRealEstateForm } from '@/shared/lib/forms/asset-form/commercial-real-estate';
import { CommodityAssetTokenForm } from '@/shared/lib/forms/asset-form/commodity-asset-token';
import { DebitAccountForm } from '@/shared/lib/forms/asset-form/debit-account';
import { DecentralizedWalletForm } from '@/shared/lib/forms/asset-form/decentralized-wallet';
import { ExchangeOptionsForm } from '@/shared/lib/forms/asset-form/exchange-options';
import { ForwardsForm } from '@/shared/lib/forms/asset-form/forwards';
import { FuturesForm } from '@/shared/lib/forms/asset-form/futures';
import { JewelryForm } from '@/shared/lib/forms/asset-form/jewelry';
import { LandForm } from '@/shared/lib/forms/asset-form/land';
import { LegalEntityForm } from '@/shared/lib/forms/asset-form/legal-entity';
import { MetalAccountForm } from '@/shared/lib/forms/asset-form/metal-account';
import { MyFundForm } from '@/shared/lib/forms/asset-form/my-fund';
import { OverTheCounterOptionsForm } from '@/shared/lib/forms/asset-form/over-the-counter-options';
import { PreciousGemstonesForm } from '@/shared/lib/forms/asset-form/precious-gemstones';
import { PublicFundForm } from '@/shared/lib/forms/asset-form/public-fund';
import { ResidentialRealEstateForm } from '@/shared/lib/forms/asset-form/residential-real-estate';
import { SecuritiesAndStocksForm } from '@/shared/lib/forms/asset-form/securities-and-stocks';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { getCollectionDtoIdPrefix } from '@/shared/lib/sj-orm/helpers';
import { AssetCategory, AssetDto, BaseSimpleAssetDto } from '@/shared/lib/sj-orm/models/assets';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';

/**
 * @deprecated Use src/entities/assets/hooks/useI18nValues.ts
 */
export const AssetCategoryToText: Record<AssetCategory, string> = {
  [AssetCategory.BANK_CELLS]: 'assets:assetTypesEnum.bank_cells',
  [AssetCategory.BONDS]: 'assets:assetTypesEnum.bonds',
  [AssetCategory.BROKERAGE_ACCOUNT]: 'assets:assetTypesEnum.brokerage_account',
  [AssetCategory.CENTRALIZED_WALLET]: 'assets:assetTypesEnum.centralized_wallet',
  [AssetCategory.COMMERCIAL_REAL_ESTATE]: 'assets:assetTypesEnum.commercial_real_estate',
  [AssetCategory.COMMODITY_ASSET_TOKENS]: 'assets:assetTypesEnum.commodity_asset_tokens',
  [AssetCategory.DEBIT_ACCOUNTS]: 'assets:assetTypesEnum.debit_accounts',
  [AssetCategory.DECENTRALIZED_WALLET]: 'assets:assetTypesEnum.decentralized_wallet',
  [AssetCategory.EXCHANGE_OPTIONS]: 'assets:assetTypesEnum.exchange_options',
  [AssetCategory.FORWARDS]: 'assets:assetTypesEnum.forwards',
  [AssetCategory.FUTURES]: 'assets:assetTypesEnum.futures',
  [AssetCategory.JEWELLERY]: 'assets:assetTypesEnum.jewellery',
  [AssetCategory.LAND]: 'assets:assetTypesEnum.land',
  [AssetCategory.LEGAL_ENTITIES]: 'assets:assetTypesEnum.legal_entities',
  [AssetCategory.METAL_ACCOUNTS]: 'assets:assetTypesEnum.metal_accounts',
  [AssetCategory.MY_FUND]: 'assets:assetTypesEnum.my_fund',
  [AssetCategory.OVER_THE_COUNTER_OPTIONS]: 'assets:assetTypesEnum.the',
  [AssetCategory.PRECIOUS_STONES]: 'assets:assetTypesEnum.precious_stones',
  [AssetCategory.PUBLIC_FUND]: 'assets:assetTypesEnum.public_fund',
  [AssetCategory.RESIDENTIAL_REAL_ESTATE]: 'assets:assetTypesEnum.residential_real_estate',
  [AssetCategory.SECURITIES_AND_STOCKS]: 'assets:assetTypesEnum.securities_and_stocks',
};

export const AssetCategoryToTextV2: Record<AssetCategory, string> = {
  [AssetCategory.BANK_CELLS]: 'assetTypesEnum.bankCells',
  [AssetCategory.BONDS]: 'assetTypesEnum.bonds',
  [AssetCategory.BROKERAGE_ACCOUNT]: 'assetTypesEnum.brokerageAccount',
  [AssetCategory.CENTRALIZED_WALLET]: 'assetTypesEnum.centralizedWallet',
  [AssetCategory.COMMERCIAL_REAL_ESTATE]: 'assetTypesEnum.commercialRealEstate',
  [AssetCategory.COMMODITY_ASSET_TOKENS]: 'assetTypesEnum.commodityAssetTokens',
  [AssetCategory.DEBIT_ACCOUNTS]: 'assetTypesEnum.debitAccounts',
  [AssetCategory.DECENTRALIZED_WALLET]: 'assetTypesEnum.decentralizedWallet',
  [AssetCategory.EXCHANGE_OPTIONS]: 'assetTypesEnum.exchangeOptions',
  [AssetCategory.FORWARDS]: 'assetTypesEnum.forwards',
  [AssetCategory.FUTURES]: 'assetTypesEnum.futures',
  [AssetCategory.JEWELLERY]: 'assetTypesEnum.jewelry',
  [AssetCategory.LAND]: 'assetTypesEnum.land',
  [AssetCategory.LEGAL_ENTITIES]: 'assetTypesEnum.legalEntities',
  [AssetCategory.METAL_ACCOUNTS]: 'assetTypesEnum.metalAccounts',
  [AssetCategory.MY_FUND]: 'assetTypesEnum.privateFund',
  [AssetCategory.OVER_THE_COUNTER_OPTIONS]: 'assetTypesEnum.otcOptions',
  [AssetCategory.PRECIOUS_STONES]: 'assetTypesEnum.preciousStones',
  [AssetCategory.PUBLIC_FUND]: 'assetTypesEnum.publicFund',
  [AssetCategory.RESIDENTIAL_REAL_ESTATE]: 'assetTypesEnum.residentialRealEstate',
  [AssetCategory.SECURITIES_AND_STOCKS]: 'assetTypesEnum.securitiesAndStocks',
};

export const AssetsForms: Record<
  AssetCategory,
  Array<TDtoWrapper<Omit<BaseSimpleAssetDto, 'id'>, NonNullable<unknown>>> | null
> = {
  [AssetCategory.BANK_CELLS]: BankCellForm,
  [AssetCategory.BONDS]: BondsForm,
  [AssetCategory.BROKERAGE_ACCOUNT]: BrokerageAccountForm,
  [AssetCategory.CENTRALIZED_WALLET]: CentralizedWalletForm,
  [AssetCategory.COMMERCIAL_REAL_ESTATE]: CommercialRealEstateForm,
  [AssetCategory.COMMODITY_ASSET_TOKENS]: CommodityAssetTokenForm,
  [AssetCategory.DEBIT_ACCOUNTS]: DebitAccountForm,
  [AssetCategory.DECENTRALIZED_WALLET]: DecentralizedWalletForm,
  [AssetCategory.EXCHANGE_OPTIONS]: ExchangeOptionsForm,
  [AssetCategory.FORWARDS]: ForwardsForm,
  [AssetCategory.FUTURES]: FuturesForm,
  [AssetCategory.JEWELLERY]: JewelryForm,
  [AssetCategory.LAND]: LandForm,
  [AssetCategory.LEGAL_ENTITIES]: LegalEntityForm,
  [AssetCategory.METAL_ACCOUNTS]: MetalAccountForm,
  [AssetCategory.MY_FUND]: MyFundForm,
  [AssetCategory.OVER_THE_COUNTER_OPTIONS]: OverTheCounterOptionsForm,
  [AssetCategory.PRECIOUS_STONES]: PreciousGemstonesForm,
  [AssetCategory.PUBLIC_FUND]: PublicFundForm,
  [AssetCategory.RESIDENTIAL_REAL_ESTATE]: ResidentialRealEstateForm,
  [AssetCategory.SECURITIES_AND_STOCKS]: SecuritiesAndStocksForm,
};

// TODO remove ?!
// export const AssetFormsTexts: Record<
//   AssetCategory,
//   Array<{ [key: string]: FormItemLabelType<unknown> }> | null
// > = {
//   [AssetCategory.BANK_CELLS]: [{}],
//   [AssetCategory.BONDS]: [{}],
//   [AssetCategory.BROKERAGE_ACCOUNT]: [{}],
//   [AssetCategory.CENTRALIZED_WALLET]: [{}],
//   [AssetCategory.COMMERCIAL_REAL_ESTATE]: [{}],
//   [AssetCategory.COMMODITY_ASSET_TOKENS]: [{}],
//   [AssetCategory.DEBIT_ACCOUNTS]: [{}],
//   [AssetCategory.DECENTRALIZED_WALLET]: [{}],
//   [AssetCategory.EXCHANGE_OPTIONS]: [{}],
//   [AssetCategory.FORWARDS]: [{}],
//   [AssetCategory.FUTURES]: [{}],
//   [AssetCategory.JEWELLERY]: [{}],
//   [AssetCategory.LAND]: [{}],
//   [AssetCategory.LEGAL_ENTITIES]: [{}],
//   [AssetCategory.METAL_ACCOUNTS]: [{}],
//   [AssetCategory.MY_FUND]: [{}],
//   [AssetCategory.OVER_THE_COUNTER_OPTIONS]: [{}],
//   [AssetCategory.PRECIOUS_STONES]: [{}],
//   [AssetCategory.PUBLIC_FUND]: [{}],
//   [AssetCategory.RESIDENTIAL_REAL_ESTATE]: [{}],
//   [AssetCategory.SECURITIES_AND_STOCKS]: [{}],
// };

export const isAssetDtoId = (id: DtoId) => {
  return id.startsWith(getCollectionDtoIdPrefix(CollectionName.ASSETS));
};

export const isAssetDto = (dto: { id: DtoId }): dto is AssetDto => {
  return 'id' in dto && typeof dto.id === 'string' && isAssetDtoId(dto.id);
};

export const ASSET_DOCUMENT_FIELDS = [
  'purchaseReceipt',
  'receipt',
  'documents',
  'additionalAppraisal',
  'internationalCertificate',
];

export const ASSET_POTENTIAL_DOCUMENT_FIELDS = new Set([
  'accessLimitations',
  'fundDocuments',
  'additionalAppraisal',
  'assetProtections',
  'coOwnerDocument',
  'depositoryStatements',
  'documents',
  'fundFounderDocument',
  'insuranceDocument',
  'personalInformation',
  'assetAcquisitions',
  'internationalCertificate',
  'otherOwnerDocuments',
  'placeOfRecordDocument',
  'ownerDocument',
  'ownershipProof',
  'annualReports',
  'assetInfoDocuments',
  'otherDocuments',
  'paymentMethods',
  'powers',
  'personalIdentifiers',
  'powersOfAttorney',
  'sourcesOfAcquisition',
  'proofsOfOwnership',
  'purchaseReceipt',
  'owner',
  'receipt',
]);

export const ASSET_POTENTIAL_ENTITY_FIELDS = new Set([
  'chooseBuilding',
  'otherValuables',
  'chooseLandPlot',
  'myAssets',
  'fundAssets',
  'jewelry',
  'attachments',
]);

export const ASSET_FIELDS_WITH_ENTITIES_IDS = new Set([
  ...ASSET_POTENTIAL_DOCUMENT_FIELDS,
  ...ASSET_POTENTIAL_ENTITY_FIELDS,
]);

export const ASSET_CURRENCY_FIELDS = [
  QuestionType.CURRENCY_CALCULATOR,
  QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
];

export const isAssetWithAmount = (data?: object): data is { amount: number } => {
  return data !== undefined && 'amount' in data;
};

export const getAssetValueMultiplier = (asset: AssetDto) =>
  isAssetWithAmount(asset) ? asset.amount : 1;
